import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Div, Typography } from '@icecreamsocial/components';
import { useParams } from 'react-router-dom';
import { useStore } from '../../../../../../../../../utils/hooks';
import ShareSettingsForm from '../../../../../../components/ShareSettingsForm';
import {
	EditFormControls,
	Pagename,
} from '../../../../../../../../presentational';
import { formatImageURL } from '../../../../../../../../../utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../../../../../../../queries/campaigns';
import { useCampaign } from '../../../../providers/Campaign';

const ShareSettings = () => {
	const store = useStore();
	const { campaignId } = useParams();
	const { campaign, update } = useCampaign();
	const { data } = campaign;
	const queryClient = useQueryClient();

	/**
	 * gets the discount data
	 */
	const { data: discount } = useQuery(
		queryKeys.getDiscounts({ campaignId }),
		async () => {
			const { discount } = await store.getCampaignDiscounts({ campaignId });
			return discount || {};
		},
		{ refetchOnWindowFocus: false }
	);

	/**
	 * updates a campaign's discount
	 * @returns
	 */
	const updateCampaignDiscountsMutation = async ({
		campaignId,
		discountCode,
		discountCodeDesc,
	}) => {
		const { discount } = await store.updateCampaignDiscounts({
			campaignId,
			discountCode,
			discountCodeDesc,
		});

		return discount;
	};

	/**
	 * mutate campaign discount data
	 */
	const { mutate: updateCampaignDiscounts } = useMutation(
		updateCampaignDiscountsMutation,
		{
			onSuccess: (data, variables) => {
				queryClient.invalidateQueries(queryKeys.getDiscounts._def);
				queryClient.setQueryData(queryKeys.getDiscounts({ campaignId }), data);
			},
		}
	);

	const initialValues = {
		conversionGoal: data?.conversionGoal,
		shareTitle: data?.shareTitle,
		shareMessage: data?.shareMessage,
		shareImage: formatImageURL(data?.shareImage),
		emailSubject: data?.emailSubject,
		emailNote: data?.emailNote,
		redirectUrl: data?.redirectUrl,
		discountCode: discount?.discountCode,
		discountCodeDesc: discount?.discountCodeDesc,
	};

	const onSubmit = async ({
		shareTitle,
		shareMessage,
		shareImage,
		emailSubject,
		emailNote,
		redirectUrl,
		discountCode,
		discountCodeDesc,
	}) => {
		await update.mutate({
			shareTitle,
			shareImage,
			emailSubject,
			shareMessage,
			emailNote,
			redirectUrl,
		});

		await updateCampaignDiscounts({
			campaignId,
			discountCode,
			discountCodeDesc,
		});
	};

	return (
		<Div>
			<Pagename>Share Settings</Pagename>
			<Typography.P>
				Please make sure that the content on this step follows the{' '}
				<Typography.A
					href='https://www.facebook.com/communitystandards/'
					target='__blank'
				>
					Facebook Community Standards
				</Typography.A>{' '}
				to prevent any posts from being flagged and removed
			</Typography.P>
			{data && (
				<ShareSettingsForm
					onSubmit={onSubmit}
					renderControls={(props) => <EditFormControls {...props} />}
					{...initialValues}
				/>
			)}
		</Div>
	);
};
ShareSettings.displayName = 'Campaigns.Campaign.Settings.General';

export default observer(ShareSettings);
